import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const fetchFooters = createAsyncThunk(
  'footers/fetchFooters',
  async (params = {}) => {
    const response = await api.get('/footers', { params });
    return response.data;
  }
);

export const fetchFooter = createAsyncThunk(
  'footers/fetchFooter',
  async (id) => {
    const response = await api.get(`/footers/${id}`);
    return response.data;
  }
);

export const createFooter = createAsyncThunk(
  'footers/createFooter',
  async (footerData) => {
    const response = await api.post('/footers', footerData);
    return response.data;
  }
);

export const updateFooter = createAsyncThunk(
  'footers/updateFooter',
  async ({ id, data }) => {
    const response = await api.put(`/footers/${id}`, data);
    return response.data;
  }
);

export const deleteFooter = createAsyncThunk(
  'footers/deleteFooter',
  async ({ id, force = false }, { rejectWithValue }) => {
    try {
      const url = force ? `/footers/${id}?force=true` : `/footers/${id}`;
      await api.delete(url);
      return id;
    } catch (error) {
      // Pass through the error response data
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const setDefaultFooter = createAsyncThunk(
  'footers/setDefaultFooter',
  async (id) => {
    const response = await api.patch(`/footers/${id}/default`);
    return response.data;
  }
);

export const duplicateFooter = createAsyncThunk(
  'footers/duplicateFooter',
  async (id) => {
    const response = await api.post(`/footers/${id}/duplicate`);
    return response.data;
  }
);

export const fetchDefaultFooter = createAsyncThunk(
  'footers/fetchDefaultFooter',
  async () => {
    const response = await api.get('/footers/default');
    return response.data;
  }
);

const footerSlice = createSlice({
  name: 'footers',
  initialState: {
    items: [],
    currentFooter: null,
    defaultFooter: null,
    loading: false,
    error: null,
    total: 0,
    page: 1,
    pages: 1
  },
  reducers: {
    clearCurrentFooter: (state) => {
      state.currentFooter = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearFooters: (state) => {
      state.items = [];
      state.currentFooter = null;
      state.defaultFooter = null;
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch footers
      .addCase(fetchFooters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFooters.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.footers;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pages = action.payload.pages;
      })
      .addCase(fetchFooters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch single footer
      .addCase(fetchFooter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFooter.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFooter = action.payload;
      })
      .addCase(fetchFooter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Create footer
      .addCase(createFooter.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
      })
      
      // Update footer
      .addCase(updateFooter.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentFooter?.id === action.payload.id) {
          state.currentFooter = action.payload;
        }
      })
      
      // Delete footer
      .addCase(deleteFooter.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.total -= 1;
        if (state.currentFooter?.id === action.payload) {
          state.currentFooter = null;
        }
      })
      .addCase(deleteFooter.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to delete footer';
      })
      
      // Set default footer
      .addCase(setDefaultFooter.fulfilled, (state, action) => {
        // Update all footers to not be default
        state.items = state.items.map(item => ({
          ...item,
          isDefault: item.id === action.payload.footer.id
        }));
        state.defaultFooter = action.payload.footer;
      })
      
      // Duplicate footer
      .addCase(duplicateFooter.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
      })
      
      // Fetch default footer
      .addCase(fetchDefaultFooter.fulfilled, (state, action) => {
        state.defaultFooter = action.payload;
      });
  }
});

export const { clearCurrentFooter, clearError, clearFooters } = footerSlice.actions;
export default footerSlice.reducer;